import React from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { Values } from '@lib/interfaces/form';
import { GraphQLFormattedError } from 'graphql';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import replaceNullsWithUndefined from '@lib/utils/replaceNullsWithUndefined';
import getUserFormFields from 'components/UserForm/utils/getUserFormFields';
import UserFormActions from 'components/UserForm/UserFormActions';
import { GetLoadOptions, LoadOptions } from '@lib/components/Select/types';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import UserFieldNames from '@lib/enums/fieldNames/userFieldNames';
import { getRolesOptions } from '@lib/utils/roles';
import styles from './UserForm.module.scss';

export interface UserFormProps {
  countriesCodes: string[];
  defaultValues?: Values;
  formRef?: React.RefObject<FormRef | undefined>;
  graphQLErrors?: readonly GraphQLFormattedError[];
  loading: boolean;
  onCancel: () => void;
  onSubmit: (values: Values) => void;
  openDepartmentsModalTableMultiselect?: () => void;
}

interface Props {
  getDepartmentsLoadOptions: GetLoadOptions;
  sectorsLoadOptions: LoadOptions;
  sitesLoadOptions: LoadOptions;
  teamsLoadOptions: LoadOptions;
}

const emptyDefaultValues = {
  id: null,
  [UserFieldNames.FirstName]: null,
  [UserFieldNames.LastName]: null,
  [UserFieldNames.Email]: null,
  [UserFieldNames.Role]: getRolesOptions()[0],
};

function UserForm(props: UserFormProps & Props) {
  const {
    countriesCodes,
    defaultValues = emptyDefaultValues,
    formRef,
    getDepartmentsLoadOptions,
    graphQLErrors,
    loading,
    onCancel,
    onSubmit,
    openDepartmentsModalTableMultiselect,
    sectorsLoadOptions,
    sitesLoadOptions,
    teamsLoadOptions,
  } = props;
  const isDeactivated = !!_get(defaultValues, 'deactivatedAt');
  const { t } = useTranslation();
  const fields = getUserFormFields({
    countriesCodes,
    getDepartmentsLoadOptions,
    openDepartmentsModalTableMultiselect,
    prevValues: defaultValues,
    sectorsLoadOptions,
    sitesLoadOptions,
    t,
    teamsLoadOptions,
  });

  return (
    <>
      {isDeactivated && (
        <div className={styles.deactivatedContent}>
          <Chip
            type={ChipTypes.chipRed}
            label={t('deactivated')}
            leftIcon="do_not_disturb_on"
            size={ChipSizes.medium}
          />
        </div>
      )}
      <Form
        formId="member"
        formRef={formRef}
        defaultValues={replaceNullsWithUndefined(defaultValues)}
        onSubmit={onSubmit}
        fields={fields}
        actionsComponent={UserFormActions}
        actionsComponentProps={{
          isEditForm: !_isEmpty(defaultValues),
          onCancel,
        }}
        graphQLErrors={graphQLErrors}
        graphQLErrorsReplacePathMap={{
          username: UserFieldNames.Username,
        }}
        loading={loading}
      />
    </>
  );
}

export default UserForm;
